// Home: today at this gate — every consignment recorded today with its receiving status, waiting
// ones first in mind (Remind on the card), plus earlier days' consignments still waiting so a truck
// left overnight is never lost. Refreshes every minute and when the phone wakes.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { DoorOpen, Hourglass, Loader2, RefreshCw, ScanQrCode, Truck } from "lucide-react";

import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { gateApi, isoDay, type ConsignmentsResponse } from "@/lib/gate";
import { useGate } from "@/lib/session-scope";
import { cn } from "@/lib/utils";
import { ConsignmentCard } from "@/components/consignment-card";
import { ConsignmentSheet } from "@/components/consignment-sheet";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

type Filter = "all" | "WAITING" | "RECEIVED";

export default function Today() {
  const [params] = useSearchParams();
  const t = params.get("t");
  // A QR scanned with the phone's own camera opens the app at /?t=<token> → the Scan page resolves it.
  if (t) return <Navigate to={`/scan?t=${encodeURIComponent(t)}`} replace />;
  return <TodayList />;
}

function TodayList() {
  const { token } = useAuth();
  const { gate } = useGate();
  const [data, setData] = useState<ConsignmentsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [openId, setOpenId] = useState<number | null>(null);
  const [nonce, setNonce] = useState(0);
  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  // Every minute (waiting times tick) and when the phone wakes / the tab comes back.
  useEffect(() => {
    const timer = setInterval(refresh, 60_000);
    const onVisible = () => document.visibilityState === "visible" && refresh();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh]);

  useEffect(() => {
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reload on gate / refresh
    setLoading(true);
    gateApi
      .consignments(token, gate.id, { day: isoDay() }, controller.signal)
      .then((d) => {
        setData(d);
        setError(null);
      })
      .catch((e) => {
        if ((e as Error).name === "AbortError") return;
        setError(e instanceof ApiError ? e.message : "Could not load today's consignments.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [token, gate.id, nonce]);

  const rows = useMemo(() => data?.rows ?? [], [data]);
  const shown = filter === "all" ? rows : rows.filter((r) => r.status === filter);
  const earlier = data?.waitingEarlier ?? [];
  const waitingNow = (data?.summary.waiting ?? 0) + earlier.length;

  return (
    <div className="flex w-full flex-col gap-4 px-4 pt-4 pb-8 md:px-6">
      {/* The gate + today's numbers. */}
      <div className="flex items-center gap-3">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <DoorOpen className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-base leading-tight font-semibold">{gate.name}</div>
          <div className="truncate text-xs text-muted-foreground">
            {[gate.operationUnit?.name, "Today"].filter(Boolean).join(" · ")}
          </div>
        </div>
        <Button variant="outline" aria-label="Refresh" className="size-11 rounded-xl bg-card" onClick={refresh}>
          {loading ? <Loader2 className="size-5 animate-spin" /> : <RefreshCw className="size-5" />}
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-2 md:max-w-xl">
        <Stat label="Trucks today" value={data?.summary.total} />
        <Stat label="Waiting" value={waitingNow} tone={waitingNow ? "text-amber-700 dark:text-amber-400" : undefined} />
        <Stat label="Received" value={data?.summary.received} tone="text-emerald-700 dark:text-emerald-400" />
      </div>

      {error ? (
        <p className="rounded-xl bg-destructive/10 p-4 text-sm text-destructive">{error}</p>
      ) : loading && !data ? (
        <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-36 w-full rounded-2xl" />
          ))}
        </div>
      ) : (
        <>
          {earlier.length > 0 && (
            <section className="flex flex-col gap-2.5">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-amber-800 dark:text-amber-300">
                <Hourglass className="size-4" /> Still waiting from earlier days
                <span className="rounded-full bg-card px-2 text-xs text-muted-foreground">{earlier.length}</span>
              </h2>
              <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {earlier.map((c) => (
                  <ConsignmentCard key={c.id} c={c} onOpen={() => setOpenId(c.id)} onChanged={refresh} />
                ))}
              </div>
            </section>
          )}

          <section className="flex flex-col gap-2.5">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="mr-auto text-sm font-semibold">Today</h2>
              {(["all", "WAITING", "RECEIVED"] as Filter[]).map((f) => (
                <Button
                  key={f}
                  variant="outline"
                  onClick={() => setFilter(f)}
                  className={cn(
                    "h-9 rounded-full bg-card px-3.5 text-sm",
                    filter === f &&
                      "border-primary bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground",
                  )}
                >
                  {f === "all" ? "All" : f === "WAITING" ? "Waiting" : "Received"}
                </Button>
              ))}
            </div>
            {shown.length === 0 ? (
              <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-border bg-card px-6 py-10 text-center">
                <Truck className="size-10 text-muted-foreground/50" />
                <p className="font-semibold">{rows.length ? "Nothing here" : "No trucks yet today"}</p>
                {!rows.length && (
                  <Button className="h-12 rounded-xl px-6" nativeButton={false} render={<Link to="/scan" />}>
                    <ScanQrCode className="size-5" /> Scan a truck's gate pass
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {shown.map((c) => (
                  <ConsignmentCard key={c.id} c={c} onOpen={() => setOpenId(c.id)} onChanged={refresh} />
                ))}
              </div>
            )}
          </section>
        </>
      )}

      <ConsignmentSheet id={openId} onClose={() => setOpenId(null)} onChanged={refresh} />
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value?: number; tone?: string }) {
  return (
    <div className="rounded-2xl border border-border bg-card px-3 py-2.5">
      <div className={cn("text-2xl leading-none font-bold tabular-nums", tone)}>{value ?? "–"}</div>
      <div className="mt-1 text-xs text-muted-foreground">{label}</div>
    </div>
  );
}
